import { Link, useLocation } from "react-router-dom";
import { routes, type RouteConfig } from "../../config/routes";
import ahLogo from "../../assets/logo.png";
import { useAuth } from "../../contexts/AuthContext";

interface SidebarItemProps {
  route: RouteConfig;
  isActive: boolean;
}

const SidebarItem = ({ route, isActive }: SidebarItemProps) => {
  const Icon = route.icon;

  return (
    <Link
      to={route.path}
      className={`flex items-center gap-3 px-6 py-3 text-[16px] font-medium transition-all duration-200 relative mx-2 rounded-[4px]
        ${
          isActive
            ? "bg-[rgba(255,193,0,0.15)] text-[#102040] border-r-[5px] border-[#f09040]"
            : "text-[#102040] hover:text-[#102040] hover:bg-gray-50"
        }
      `}
    >
      <Icon
        size={22}
        strokeWidth={1.5}
        className={isActive ? "text-[#102040]" : "text-[#102040]"}
      />
      <span>{route.label}</span>
    </Link>
  );
};

// AH Planning Logo Component
const AHPlanningLogo = () => (
  <img
    src={ahLogo}
    alt="AH Planning Logo"
    className="h-12 w-auto max-w-[220px] object-contain drop-shadow-sm"
  />
);

import { X } from "lucide-react";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const { hasPermission } = useAuth();

  // Filter function
  const canAccess = (route: RouteConfig) => {
    return !route.permission || hasPermission(route.permission);
  };

  // Filter routes by section
  const mainNavItems = routes.filter(
    (route) => route.section === "main" && canAccess(route),
  );
  const contentNavItems = routes.filter(
    (route) => route.section === "content" && canAccess(route),
  );
  const financialNavItems = routes.filter(
    (route) => route.section === "financial" && canAccess(route),
  );
  const settingsNavItems = routes.filter(
    (route) => route.section === "settings" && canAccess(route),
  );
  const marketingNavItems = routes.filter(
    (route) => route.section === "marketing" && canAccess(route),
  );
  const securityNavItems = routes.filter(
    (route) => route.section === "security" && canAccess(route),
  );

  // Check if a route is active based on current location
  const isRouteActive = (routePath: string) => {
    return (
      location.pathname === routePath ||
      location.pathname.startsWith(routePath + "/")
    );
  };

  return (
    <aside
      className={`
        fixed inset-y-0 left-0 z-30 w-[270px] bg-white border-r border-gray-100 flex flex-col font-['DM_Sans'] transition-transform duration-300 ease-in-out lg:translate-x-0 lg:sticky lg:top-0 lg:h-screen
        ${isOpen ? "translate-x-0" : "-translate-x-full"}
      `}
    >
      {/* Logo Section */}
      <div className="pt-8 pb-6 flex flex-col items-center justify-center relative">
        <div className="flex flex-col items-center gap-2">
          <AHPlanningLogo />
        </div>

        {/* Mobile Close Button */}
        <button
          onClick={onClose}
          className="lg:hidden absolute right-4 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
        >
          <X size={20} />
        </button>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 flex flex-col gap-1 px-2 overflow-y-auto no-scrollbar">
        {/* Main Section */}
        {mainNavItems.map((route) => (
          <SidebarItem
            key={route.path}
            route={route}
            isActive={isRouteActive(route.path)}
          />
        ))}

        {/* Content Section */}
        {contentNavItems.length > 0 && (
          <div className="px-4 pt-6 pb-2">
            <p className="text-[13px] font-medium text-[#989797] tracking-wide">
              CONTENT
            </p>
          </div>
        )}
        {contentNavItems.map((route) => (
          <SidebarItem
            key={route.path}
            route={route}
            isActive={isRouteActive(route.path)}
          />
        ))}

        {/* Financial Section */}
        {financialNavItems.length > 0 && (
          <div className="px-4 pt-6 pb-2">
            <p className="text-[13px] font-medium text-[#989797] tracking-wide">
              FINANCIAL
            </p>
          </div>
        )}
        {financialNavItems.map((route) => (
          <SidebarItem
            key={route.path}
            route={route}
            isActive={isRouteActive(route.path)}
          />
        ))}

        {/* Settings Section */}
        {settingsNavItems.length > 0 && (
          <div className="px-4 pt-6 pb-2">
            <p className="text-[13px] font-medium text-[#989797] tracking-wide">
              SETTINGS
            </p>
          </div>
        )}
        {settingsNavItems.map((route) => (
          <SidebarItem
            key={route.path}
            route={route}
            isActive={isRouteActive(route.path)}
          />
        ))}

        {/* Marketing Section */}
        {marketingNavItems.length > 0 && (
          <div className="px-4 pt-6 pb-2">
            <p className="text-[13px] font-medium text-[#989797] tracking-wide">
              MARKETING
            </p>
          </div>
        )}
        {marketingNavItems.map((route) => (
          <SidebarItem
            key={route.path}
            route={route}
            isActive={isRouteActive(route.path)}
          />
        ))}

        {/* Security Section */}
        {securityNavItems.length > 0 && (
          <div className="px-4 pt-6 pb-2">
            <p className="text-[13px] font-medium text-[#989797] tracking-wide">
              SECURITY
            </p>
          </div>
        )}
        {securityNavItems.map((route) => (
          <SidebarItem
            key={route.path}
            route={route}
            isActive={isRouteActive(route.path)}
          />
        ))}
      </nav>
    </aside>
  );
}
